import React, { ReactElement } from 'react';
import { Button, Flex, Box, Text, useDisclosure, Spinner, Tooltip } from '@chakra-ui/react';
import { ArrowUpRight, Clock, Info } from 'phosphor-react';
import { AddIcon } from '@chakra-ui/icons';
import RequestLiveModeForm from '@/screens/environment-toggle-bar/components/RequestLiveModeForm';
import { useGetUsageMetrics } from '@/screens/environment-toggle-bar/queries/useGetUsageMetrics';
import { useEnvironmentToggle } from '@/screens/environment-toggle-bar/hooks/useEnvironmentToggle';
import { useRecoilValue } from 'recoil';
import { EnvToggleAtom } from '@/states/envToggleAtom';
import { useHistory } from 'react-router-dom';

const LiveModeRequestButton = (): ReactElement => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const history = useHistory();
  const { data, isLoading } = useGetUsageMetrics();
  const { isPrepaidAccount, liveStatus, isLiveEnabled, isPostpaidAccount } = useEnvironmentToggle();
  const toggledEnv = useRecoilValue(EnvToggleAtom);

  const onAddCredits = (): void => {
    if (isLiveMode && isPrepaidAccount) {
      history.push('/settings/billing');
    }
  };
  const isLiveMode = toggledEnv === 'live';
  const isPending = liveStatus === 'PENDING';
  const shouldShowButton = !isLiveEnabled;
  const renderCreditsSection = (): ReactElement => {
    const creditsLeft = (data?.lifeTimeAllocated || 0) - (data?.lifeTimeUsage || 0);
    if (isLiveMode) {
      if (isPrepaidAccount) {
        return (
          <Flex align="center" gridGap={2.5} h="full">
            <Text color="blue.400" fontSize="sm" mr={2} fontWeight="400" fontFamily="Lexend" fontStyle="normal">
              {creditsLeft}
            </Text>

            <Button
              size="sm"
              w="4"
              h="4"
              minW={4}
              p={0}
              borderRadius="full"
              onClick={onAddCredits}
              _hover={{ bgColor: 'gray.100' }}
              bg="blue.400"
              color="white"
            >
              <AddIcon color="white" boxSize={2} />
            </Button>
          </Flex>
        );
      }
      return <></>;
    }
    return (
      <Text color="blue.400" fontSize="sm" mr={2} fontWeight="400" fontFamily="Lexend" fontStyle="normal">
        {' '}
        {data?.lifeTimeUsage} / {data?.lifeTimeAllocated}
      </Text>
    );
  };
  return (
    <Flex align="center" gap={3}>
      {shouldShowButton &&
        (isPending ? (
          <Tooltip label="Live mode request under review. We'll notify you as soon as it's approved" placement="bottom">
            <Button
              variant="outline"
              bg="orange.100"
              color="orange.500"
              borderRadius="lg"
              px={2}
              boxShadow="sm"
              fontSize="xs"
              fontWeight="medium"
              //   colorScheme="white
              borderColor="orange.500"
              opacity={0.6}
              rightIcon={<Clock size={18} fill="orange.500" />}
              mr={1}
              border="1px solid"
            >
              Go Live
            </Button>
          </Tooltip>
        ) : (
          <Button
            variant="solid"
            size="md"
            rightIcon={<ArrowUpRight size={18} />}
            bg="blue.500"
            color="white"
            borderRadius="lg"
            px={6}
            h="10"
            onClick={onOpen}
            boxShadow="sm"
            fontSize="xs"
            fontWeight="medium"
            colorScheme="blue.500"
            border="1px solid"
          >
            Go Live
          </Button>
        ))}
      <Box
        bg="white.50"
        borderRadius="lg"
        px={4}
        py={2}
        display={isLiveMode && isPostpaidAccount ? 'none' : 'flex'}
        alignItems="center"
        boxShadow="sm"
      >
        <Text color="gray.600" fontSize="sm" mr={2} fontWeight="300" fontFamily="Lexend" fontStyle="normal">
          {isLiveMode ? 'Credits' : 'Free API calls:'}
        </Text>
        {isLoading ? <Spinner size="xs" /> : renderCreditsSection()}
        {shouldShowButton && (
          <Tooltip
            label={
              <Box color="gray.600">
                If you want to add more API calls,{' '}
                <Box as="a" href="mailto:support@bureau.id" color="blue.500" textDecoration="underline">
                  Contact us
                </Box>
              </Box>
            }
            placement="bottom"
            fontSize="xs"
            bg="white"
          >
            <Box as="span" ml={1} color="gray.400">
              <Info size={16} />
            </Box>
          </Tooltip>
        )}
      </Box>
      <RequestLiveModeForm isOpen={isOpen} onClose={onClose} />
    </Flex>
  );
};

export default LiveModeRequestButton;
